import type { Feature, FeatureCollection, Geometry } from "geojson";

// Logic for the group overview map. No map library and no turf bundle (its ESM dependencies stop Jest loading it), so this file tests as plain functions.

export type GeometryKind = "polygon" | "line" | "point";
export type BBox = [number, number, number, number];

export interface OverviewAssetInput {
  id: string;
  name?: string | null;
  /** One of the asset icon keys (wind, sun, water, nuclear), where the asset was given one */
  icon?: string | null;
  /** Storage key of the asset's picture, unsigned */
  imageUrl?: string | null;
  lat?: number | string | null;
  lng?: number | string | null;
  geojson?: unknown;
}

export interface OverviewFeatureProps {
  assetId: string;
  name: string;
  kind: GeometryKind;
  /** Higher draws on top; polygons carry their negative extent so a small one is never buried under a large one */
  sortKey: number;
}

export interface OverviewAssetSummary {
  id: string;
  name: string;
  icon: string | null;
  imageUrl: string | null;
  kinds: GeometryKind[];
  primaryKind: GeometryKind | null;
  bbox: BBox | null;
}

export interface OverviewModel {
  collection: FeatureCollection<Geometry, OverviewFeatureProps>;
  assets: OverviewAssetSummary[];
  bounds: BBox | null;
  /** Assets per kind, not features: a three-part river is one line */
  counts: Record<GeometryKind, number>;
}

export interface FitPadding {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

// Drawn bottom to top, which is also the order a multi-kind asset reports its primary kind in
export const GEOMETRY_KINDS: GeometryKind[] = ["polygon", "line", "point"];

// Named for the shape, not the thing: assets carry no type yet, so a polygon may be a watershed or a solar farm
export const GEOMETRY_KIND_LABEL: Record<GeometryKind, string> = {
  polygon: "Regions",
  line: "Lines",
  point: "Points",
};

export interface OverviewAssetSection {
  /** null holds the assets with nothing to draw */
  kind: GeometryKind | null;
  label: string;
  assets: OverviewAssetSummary[];
}

/** The asset list split by what each asset draws as, empty sections left out. */
export const groupAssetsByKind = (
  assets: OverviewAssetSummary[] = []
): OverviewAssetSection[] => {
  const sections: OverviewAssetSection[] = [
    ...GEOMETRY_KINDS.map((kind) => ({
      kind,
      label: GEOMETRY_KIND_LABEL[kind],
      assets: assets.filter((asset) => asset.primaryKind === kind),
    })),
    {
      kind: null,
      label: "Not on map",
      assets: assets.filter((asset) => !asset.primaryKind),
    },
  ];
  return sections.filter((section) => section.assets.length > 0);
};

// A click lands on the smallest thing under it: a dam sits on its river, which runs through its watershed
const HIT_PRIORITY: Record<GeometryKind, number> = {
  point: 3,
  line: 2,
  polygon: 1,
};

const KIND_BY_TYPE: Record<string, GeometryKind> = {
  Polygon: "polygon",
  MultiPolygon: "polygon",
  LineString: "line",
  MultiLineString: "line",
  Point: "point",
  MultiPoint: "point",
};

export const classifyGeometry = (
  type: string | undefined | null
): GeometryKind | null => (type && KIND_BY_TYPE[type]) || null;

const toFiniteNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

/** A geometry's extent, or null when any of it cannot be read as a position. */
export const geometryBounds = (
  geometry: Geometry | null | undefined
): BBox | null => {
  const coordinates = (geometry as { coordinates?: unknown } | null | undefined)
    ?.coordinates;
  if (!Array.isArray(coordinates)) return null;

  const box: BBox = [Infinity, Infinity, -Infinity, -Infinity];
  let valid = true;

  const walk = (node: unknown) => {
    if (!valid) return;
    if (!Array.isArray(node)) {
      valid = false;
      return;
    }
    if (typeof node[0] === "number") {
      const [lng, lat] = node as number[];
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
        valid = false;
        return;
      }
      box[0] = Math.min(box[0], lng);
      box[1] = Math.min(box[1], lat);
      box[2] = Math.max(box[2], lng);
      box[3] = Math.max(box[3], lat);
      return;
    }
    node.forEach(walk);
  };

  walk(coordinates);
  return valid && box.every((value) => Number.isFinite(value)) ? box : null;
};

// Only ever compared against another polygon's, so the extent stands in for the true area
const extentOf = (box: BBox | null): number =>
  box ? (box[2] - box[0]) * (box[3] - box[1]) : 0;

const mergeBounds = (a: BBox | null, b: BBox | null): BBox | null => {
  if (!a) return b;
  if (!b) return a;
  return [
    Math.min(a[0], b[0]),
    Math.min(a[1], b[1]),
    Math.max(a[2], b[2]),
    Math.max(a[3], b[3]),
  ];
};

// AWSJSON arrives as a string, and some rows were stringified twice on the way in
const parseGeojson = (raw: unknown): any => {
  let value = raw;
  for (let pass = 0; pass < 2 && typeof value === "string"; pass += 1) {
    try {
      value = JSON.parse(value);
    } catch {
      return null;
    }
  }
  return value && typeof value === "object" ? value : null;
};

/** Every geometry an asset's GeoJSON holds, whatever it is wrapped in. */
export const extractGeometries = (raw: unknown): Geometry[] => {
  const walk = (node: any): Geometry[] => {
    if (!node || typeof node !== "object") return [];
    if (node.type === "FeatureCollection")
      return (node.features || []).flatMap(walk);
    if (node.type === "Feature") return walk(node.geometry);
    if (node.type === "GeometryCollection")
      return (node.geometries || []).flatMap(walk);
    return classifyGeometry(node.type) && Array.isArray(node.coordinates)
      ? [node as Geometry]
      : [];
  };
  return walk(parseGeojson(raw));
};

const pointFromLatLng = (asset: OverviewAssetInput): Geometry | null => {
  const lat = toFiniteNumber(asset.lat);
  const lng = toFiniteNumber(asset.lng);
  return lat === null || lng === null
    ? null
    : { type: "Point", coordinates: [lng, lat] };
};

export const buildOverviewModel = (
  assets: OverviewAssetInput[] = []
): OverviewModel => {
  const features: Feature<Geometry, OverviewFeatureProps>[] = [];
  const summaries: OverviewAssetSummary[] = [];
  const counts: Record<GeometryKind, number> = {
    polygon: 0,
    line: 0,
    point: 0,
  };
  let bounds: BBox | null = null;

  assets.forEach((asset) => {
    if (!asset?.id) return;
    const name = asset.name || "Untitled asset";

    // A geometry that cannot be measured cannot be drawn either, so it is dropped before the fallback is considered
    let geometries = extractGeometries(asset.geojson).filter((geometry) =>
      geometryBounds(geometry)
    );
    if (geometries.length === 0) {
      const fallback = pointFromLatLng(asset);
      geometries = fallback ? [fallback] : [];
    }

    const kinds = new Set<GeometryKind>();
    let assetBounds: BBox | null = null;

    geometries.forEach((geometry) => {
      const kind = classifyGeometry(geometry.type);
      if (!kind) return;
      const geometryBox = geometryBounds(geometry);
      kinds.add(kind);
      assetBounds = mergeBounds(assetBounds, geometryBox);
      features.push({
        type: "Feature",
        geometry,
        properties: {
          assetId: asset.id,
          name,
          kind,
          sortKey: kind === "polygon" ? -extentOf(geometryBox) : 0,
        },
      });
    });

    const orderedKinds = GEOMETRY_KINDS.filter((kind) => kinds.has(kind));
    orderedKinds.forEach((kind) => {
      counts[kind] += 1;
    });
    bounds = mergeBounds(bounds, assetBounds);
    summaries.push({
      id: asset.id,
      name,
      icon: asset.icon || null,
      imageUrl: asset.imageUrl || null,
      kinds: orderedKinds,
      primaryKind: orderedKinds[0] ?? null,
      bbox: assetBounds,
    });
  });

  return {
    collection: { type: "FeatureCollection", features },
    assets: summaries,
    bounds,
    counts,
  };
};

// Each pick is a plot of its own in the card, stacked, and a handful is as many as a reader will scroll through. Past that the sites belong on one chart together, not in more plots.
export const MAX_SELECTION = 5;

/**
 * The selection after a click, oldest pick first. A plain click picks the one asset, or clears
 * on empty map. With Ctrl or Cmd held the click adds to what is picked or takes from it, and a
 * pick past the limit pushes the oldest one out.
 */
export const nextSelection = (
  current: string[],
  id: string | null,
  additive: boolean
): string[] => {
  if (!additive) return id ? [id] : [];
  // Holding the key and missing the asset is a slip, not a request to clear
  if (!id) return current;
  if (current.includes(id)) return current.filter((picked) => picked !== id);
  return [...current, id].slice(-MAX_SELECTION);
};

/** Which asset a click means, from everything under the pointer. */
export const pickAssetId = (
  hits: Array<{ properties?: Record<string, unknown> | null }> = []
): string | null => {
  let best: { id: string; priority: number; sortKey: number } | null = null;

  hits.forEach((hit) => {
    const id = hit.properties?.assetId;
    const kind = hit.properties?.kind as GeometryKind | undefined;
    if (typeof id !== "string" || !kind || !HIT_PRIORITY[kind]) return;
    const candidate = {
      id,
      priority: HIT_PRIORITY[kind],
      sortKey: toFiniteNumber(hit.properties?.sortKey) ?? 0,
    };
    if (
      !best ||
      candidate.priority > best.priority ||
      (candidate.priority === best.priority && candidate.sortKey > best.sortKey)
    ) {
      best = candidate;
    }
  });

  return best ? (best as { id: string }).id : null;
};

/** Whether a screen point falls in the part of the map the card and controls leave showing. */
export const isWithinView = (
  point: { x: number; y: number },
  size: { width: number; height: number },
  inset: Partial<FitPadding> = {}
): boolean =>
  point.x >= (inset.left ?? 0) &&
  point.x <= size.width - (inset.right ?? 0) &&
  point.y >= (inset.top ?? 0) &&
  point.y <= size.height - (inset.bottom ?? 0);

/** Where a hover tooltip goes: below and right of the pointer, flipped to the other side of it at the map's right and bottom edges. */
export const placeTooltip = (
  point: { x: number; y: number },
  size: { width: number; height: number },
  tip: { width: number; height: number },
  offset = 14
): { left: number; top: number } => {
  const fitsRight = point.x + offset + tip.width <= size.width;
  const fitsBelow = point.y + offset + tip.height <= size.height;
  return {
    left: Math.max(
      0,
      fitsRight ? point.x + offset : point.x - offset - tip.width
    ),
    top: Math.max(
      0,
      fitsBelow ? point.y + offset : point.y - offset - tip.height
    ),
  };
};

// The least map worth framing anything in
const MINIMUM_VIEW = 120;

// Along one axis, the card's inset at the start and the controls' at the end. What the axis cannot afford goes in the order it costs the reader least: an asset tight to the edge still shows, one under a button mostly shows, one under the card does not show at all.
const fitAxis = (
  length: number,
  startInset: number,
  endInset: number,
  base: number
): [number, number] => {
  const margins = [base, base / 2, 16, 0].filter((margin) => margin <= base);
  const insets: Array<[number, number]> = [
    [startInset, endInset],
    [startInset, 0],
    [0, 0],
  ];
  for (const [startBy, endBy] of insets) {
    for (const margin of margins) {
      const start = margin + startBy;
      const end = margin + endBy;
      if (length - start - end >= MINIMUM_VIEW) return [start, end];
    }
  }
  return [0, 0];
};

/**
 * Camera padding that frames assets in the map the floating card and the controls leave showing.
 * The map refuses to move at all when padding leaves no room, so what the canvas cannot afford is
 * given up in order: first the margin, then the controls' inset, and the card's inset last.
 */
export const clampFitPadding = (
  size: { width: number; height: number },
  inset: Partial<FitPadding> = {},
  base = 48
): FitPadding => {
  const [left, right] = fitAxis(
    size.width,
    inset.left ?? 0,
    inset.right ?? 0,
    base
  );
  const [top, bottom] = fitAxis(
    size.height,
    inset.top ?? 0,
    inset.bottom ?? 0,
    base
  );
  return { left, top, right, bottom };
};
